import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import QRCode from 'qrcode.react'
import { BeatLoader } from 'react-spinners'

import brandingIcon from '@theme/icon/branding.png'
import { useToastAlert } from 'hooks/useToastAlert'
import { useHtml2Image } from 'hooks/useHtml2Image'
import { selectShareVideoData } from 'redux/selector/shareVideo'
import { shareVideoActions } from 'redux/slice/shareVideo'
import Base64Image from 'component/Base64Image'
import useHintModal from 'hooks/useHintModal'

import {
  BtnCancel,
  BrandIcon,
  ButtonRow,
  Card,
  CopyButton,
  Cover,
  CoverErrorHint,
  CoverWrapper,
  Wrapper,
  HeaderRow,
  HeaderTitle,
  QrBox,
  QrRow,
  QrText,
  SavedToast,
  SecondaryButton,
} from './Styled'

interface ShareVideoModalProps {
  /** 影片標題，顯示於卡片頂部 */
  title?: string
  /** 已解碼的封面（自列表帶入時才有），優先使用以免重解一次 */
  posterImg?: string
  /**
   * 封面原始來源，posterImg 不存在時由 Base64Image 自行解碼。
   * 短視頻傳的是直向的 coverphoto_v，交由 16:9 容器置中裁切，卡片版面不變。
   */
  coverphoto?: string
}

/**
 * 分享連結長逾千字元，糾錯等級直接決定模組數：
 * H 會撐到 177 模組，在手機卡片寬度下每模組不足 1 px、無法掃描；
 * L 約 121 模組，同樣寬度下才有足夠的每模組像素。
 * 這是螢幕顯示用的 QR，不需要印刷等級的容錯。
 */
const QR_ERROR_CORRECTION = 'L' as const

/** canvas 實際渲染邊長；不給 size 會用預設 128 再被 CSS 放大而糊掉 */
const QR_SIZE_PX = 512

/** 封面就緒狀態：兩條封面分支（posterImg / Base64Image）共用同一組狀態機 */
type CoverStatus = 'loading' | 'ready' | 'error'

export default function ShareVideoModal({ title, posterImg, coverphoto }: ShareVideoModalProps) {
  const dispatch = useDispatch()
  const { closeHintModal } = useHintModal()
  const { shareUrl } = useSelector(selectShareVideoData)
  const { toast } = useToastAlert()

  const { downloadHtmlAsImage } = useHtml2Image()

  /** 產圖失敗：讓使用者看得見並可再按一次，而非靜默無反應 */
  const [saveFailed, setSaveFailed] = useState(false)
  /**
   * 產圖進行中。html2canvas 對整張卡片（含封面）以 scale≥2 渲染極耗記憶體，
   * 連點會並行產生多張大型 canvas，低階機型容易瞬間 OOM 閃退。
   */
  const [isSaving, setIsSaving] = useState(false)
  const cardContentRef = useRef<HTMLDivElement>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 封面就緒狀態：posterImg（<Cover>）與 coverphoto（<Base64Image>）兩條分支都會回報到這裡，
  // 保存鍵只在 'ready' 時可用，避免慢網路下截出空白封面卻顯示保存成功。
  const [coverStatus, setCoverStatus] = useState<CoverStatus>('loading')
  // 用於強制 <Cover>/<Base64Image> 重新掛載以觸發重試（remount 會讓兩者各自重新載入/重新 fetch）
  const [coverRetryKey, setCoverRetryKey] = useState(0)

  // 來源（posterImg 或 coverphoto）變更時 reset 就緒狀態；若兩者皆無來源，視為無需等待，直接 ready，
  // 避免在理論上沒有封面可載的情況下把保存鍵永久卡在 disabled。
  useEffect(() => {
    setCoverStatus(posterImg || coverphoto ? 'loading' : 'ready')
  }, [posterImg, coverphoto])

  const handleCoverLoad = useCallback(() => setCoverStatus('ready'), [])
  const handleCoverError = useCallback(() => setCoverStatus('error'), [])
  const handleRetryCover = useCallback(() => {
    setCoverStatus('loading')
    setCoverRetryKey((key) => key + 1)
  }, [])

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  // 卡片卸載即清除分享狀態。HintModal 的 overlay 點擊只會 closeHintModal，
  // 不清的話 shareUrl 會殘留，同一部影片再次分享時 shareUrl 不變、
  // ViewVideo 的 effect 不會重跑，modal 就打不開了。
  useEffect(() => {
    return () => {
      dispatch(shareVideoActions.clearShareData())
    }
  }, [dispatch])

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl)
        toast('已复制链接成功')
        return
      }
      throw new Error('clipboard unavailable')
    } catch {
      // 內嵌 WebView 可能沒有 navigator.clipboard，退回舊版做法而不是靜默失敗
      const textArea = document.createElement('textarea')
      let copied = false
      try {
        textArea.value = shareUrl
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        // execCommand 失敗時回傳 false 而非拋錯，必須檢查回傳值，否則會誤報成功
        copied = document.execCommand('copy')
      } catch {
        copied = false
      } finally {
        textArea.remove()
      }
      toast(copied ? '已复制链接成功' : '复制失败')
    }
  }, [shareUrl, toast])

  /**
   * 對整張卡片產圖，含兩顆操作按鈕——輸出圖與使用者在畫面上看到的卡片一致。
   * 唯一排除的是「保存成功」提示（SavedToast 帶 data-html2canvas-ignore）。
   * 下載機制與身份卡共用 useHtml2Image：App 內走原生 handler 存相簿，其餘走 <a download>。
   */
  const handleSaveImage = useCallback(async () => {
    // 封面未就緒時不產圖：按鈕本身雖已 disabled 擋掉點擊，這裡再擋一層防止意外呼叫
    if (coverStatus !== 'ready') return
    if (isSaving) return

    // 產圖可能失敗（低階機型在 scale≥2、capture 範圍含封面時可能 OOM）。
    // 不接住的話會變成 unhandled rejection，使用者只看到「按了沒反應」，
    // 既無提示也無法重試。
    try {
      setSaveFailed(false)
      setIsSaving(true)
      await downloadHtmlAsImage(cardContentRef.current, 'share_qrcode', {
        // html2canvas 未指定 scale 時預設用 DPR，DPR=1 的 WebView 等於沒改善，
        // 故用 Math.max 保底拉到至少 2，讓 QR 經通訊軟體二次壓縮後仍可辨識
        scale: Math.max(window.devicePixelRatio || 1, 2),
        // Card 有 max-height + overflow-y:auto，矮螢幕（或長標題）會讓卡片內部捲動，
        // html2canvas 只渲染元素當下的可視框 —— 實測在 390x420 下輸出圖只有 clientHeight 那一截，
        // 而且內容取決於當時捲到哪裡（封面與兩顆按鈕會一起消失）。
        // 在 onclone 解除限制：只改離屏複本，不擾動使用者正在看的 DOM，
        // 因此不會出現卡片瞬間攤開、內容被推出視窗的跳動。
        onclone: (_doc, element) => {
          element.style.maxHeight = 'none'
          element.style.overflowY = 'visible'

          // html2canvas 1.4.1 完全不處理 object-fit（dist 內無任何相關實作），
          // 會把圖片拉伸填滿元素框——短影片的直向封面因此在輸出圖裡被壓扁成 16:9。
          // 改以 background-image + background-size: cover 呈現（html2canvas 有支援），
          // 即可得到與畫面上一致的置中裁切。
          //
          // ⚠️ 背景必須設在 <img> 的「直接父層」，不能設在外層的 CoverWrapper：
          // Base64Image 的容器自帶「图片加载中」佔位背景，隱藏 <img> 會讓它露出來，
          // 而外層容器的繪製順序比它更早，新背景反而會被佔位圖蓋住（實測確認）。
          // 因此除了把背景設在最靠近圖片的那一層，也要清掉上一層的佔位背景。
          const coverImg = element.querySelector<HTMLImageElement>('[data-share-cover] img')
          const imageHolder = coverImg?.parentElement
          if (coverImg?.src && imageHolder) {
            imageHolder.style.backgroundImage = `url("${coverImg.src}")`
            imageHolder.style.backgroundSize = 'cover'
            imageHolder.style.backgroundPosition = 'center'
            imageHolder.style.backgroundRepeat = 'no-repeat'
            // 只清 background-image，背景色（background-color）不受影響
            if (imageHolder.parentElement) imageHolder.parentElement.style.backgroundImage = 'none'
            coverImg.style.display = 'none'
          }
        },
      })
    } catch {
      setSaveFailed(true)
      return
    } finally {
      setIsSaving(false)
    }

  }, [coverStatus, isSaving, downloadHtmlAsImage])

  return (
    <Wrapper>
      <Card ref={cardContentRef}>
        <HeaderRow>
          <BrandIcon src={brandingIcon} alt="" />
          <HeaderTitle>{title}</HeaderTitle>
        </HeaderRow>

        <CoverWrapper data-share-cover="true">
          {posterImg ? (
            <Cover key={coverRetryKey} src={posterImg} alt="" onLoad={handleCoverLoad} onError={handleCoverError} />
          ) : (
            coverphoto && (
              <Base64Image
                key={coverRetryKey}
                src={coverphoto}
                aspectRatio={16 / 9}
                noLazy
                onLoad={handleCoverLoad}
                onError={handleCoverError}
              />
            )
          )}
          {coverStatus === 'error' && (
            <CoverErrorHint
              type="button"
              onClick={handleRetryCover}
              data-testid="share_video_cover_retry_button"
              aria-label="封面加载失败，点击重试"
            >
              封面加载失败，点击重试
            </CoverErrorHint>
          )}
        </CoverWrapper>

        <QrRow>
          <QrBox>
            {shareUrl ? (
              <QRCode size={QR_SIZE_PX} value={shareUrl} level={QR_ERROR_CORRECTION} />
            ) : (
              <BeatLoader size={8} />
            )}
          </QrBox>
          <QrText>
            扫描二维码下载APP观看更多
          </QrText>
        </QrRow>

        <ButtonRow>
          <SecondaryButton
            onClick={handleSaveImage}
            disabled={!shareUrl || coverStatus !== 'ready' || isSaving}
          >
            {isSaving ? '生成中…' : saveFailed && coverStatus === 'ready' ? '保存失败，重试' : '保存图片分享'}
          </SecondaryButton>
          <CopyButton onClick={handleCopy} disabled={!shareUrl}>
            复制分享链接
          </CopyButton>
        </ButtonRow>
      </Card>
      <BtnCancel data-testid="share_video_modal_close" aria-label="share_video_modal_close" onClick={closeHintModal} />
    </Wrapper>
  )
}
